import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Upload, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import FramedProductImage from "../components/FramedProductImage";
import api, { formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";

const emptyVar = () => ({ name: "", sku: "", price: "", promo_price: "", stock: 0, images: [] });

function ImageInput({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd);
      onChange(r.data.url);
      toast.success("Imagem enviada");
    } catch (err) {
      toast.error(formatApiError(err, "Falha no upload"));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex gap-2">
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        placeholder="URL da imagem ou faça upload" className="bg-[#141414] border-white/10 focus:border-amber-500" />
      <label className="shrink-0 cursor-pointer inline-flex items-center gap-1.5 px-3 border border-gold/40 text-gold rounded-md text-xs hover:bg-amber-500/10 transition-colors" data-testid={`${testid}-upload`}>
        <Upload size={13} /> {uploading ? "..." : "Upload"}
        <input type="file" accept="image/*" className="hidden" onChange={upload} />
      </label>
    </div>
  );
}

export default function AdminProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshBootstrap } = useStore();
  const isNew = !id;
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [globalPrice, setGlobalPrice] = useState({ dy: 0, dx: 0, scale: 1, color: "#241600", width: 52, height: 6, radius: 2 });
  const [form, setForm] = useState({
    name: "", slug: "", description: "", type: "physical", category_id: "",
    images: [""], variations: [emptyVar()], digital_url: "",
    featured: false, active: true, meta_title: "", meta_description: "",
    image_adjust: { fit: "contain", x: 0, y: 0, scale: 1 },
    card_text: "",
    price_adjust: null,
    price_custom: false,
  });

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setGlobalPrice({
        dy: r.data.card_price_dy || 0,
        dx: r.data.card_price_dx || 0,
        scale: r.data.card_price_scale || 1,
        color: r.data.card_price_color || "#241600",
        width: r.data.card_price_width || 52,
        height: r.data.card_price_height || 6,
        radius: r.data.card_price_radius ?? 2,
      });
    }).catch(() => {});
    if (!isNew) {
      api.get("/admin/products").then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (p) {
          setForm({
            ...p,
            image_adjust: p.image_adjust || { fit: "contain", x: 0, y: 0, scale: 1 },
            card_text: p.card_text || "",
            price_adjust: p.price_adjust || { dy: 0, dx: 0, scale: 1, color: "#241600", width: 52, height: 6, radius: 2 },
            price_custom: !!p.price_adjust,
            images: p.images?.length ? p.images : [""],
            variations: (p.variations || []).map((v) => ({
              ...v,
              price: (v.price / 100).toFixed(2).replace(".", ","),
              promo_price: v.promo_price ? (v.promo_price / 100).toFixed(2).replace(".", ",") : "",
            })),
            digital_url: p.digital_url || "",
          });
        }
      }).catch(() => {});
    }
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVar = (i, k, v) => setForm((f) => {
    const variations = [...f.variations];
    variations[i] = { ...variations[i], [k]: v };
    return { ...f, variations };
  });

  const parsePrice = (s) => {
    if (s === "" || s == null) return null;
    const n = Math.round(parseFloat(String(s).replace(",", ".")) * 100);
    return Number.isFinite(n) ? n : null;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_adjust: form.price_custom ? form.price_adjust : null,
        images: form.images.filter(Boolean),
        variations: form.variations.filter((v) => v.name && v.sku).map((v) => ({
          id: v.id, name: v.name, sku: v.sku,
          price: parsePrice(v.price) ?? 0,
          promo_price: parsePrice(v.promo_price),
          stock: parseInt(v.stock) || 0,
          images: (v.images || []).filter(Boolean),
        })),
        digital_url: form.type === "digital" ? form.digital_url : null,
      };
      delete payload.category;
      delete payload.price_custom;
      if (isNew) {
        await api.post("/admin/products", payload);
        toast.success("Produto criado");
      } else {
        await api.put(`/admin/products/${id}`, payload);
        toast.success("Produto salvo");
      }
      navigate("/admin/produtos");
    } catch (err) {
      toast.error(formatApiError(err, "Falha ao salvar"));
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-product-edit">
      <Button variant="ghost" onClick={() => navigate("/admin/produtos")} className="text-zinc-400 hover:text-gold mb-6 -ml-3" data-testid="back-to-products">
        <ArrowLeft size={15} className="mr-1.5" /> Produtos
      </Button>
      <h1 className="font-display text-3xl tracking-tight mb-10">{isNew ? "Novo produto" : "Editar produto"}</h1>

      <form onSubmit={save} className="max-w-4xl space-y-12">
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Label>Nome *</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input"
              className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
          </div>
          <div>
            <Label>Slug (URL)</Label>
            <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="gerado automaticamente" data-testid="product-slug-input"
              className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
          </div>
          <div>
            <Label>Categoria *</Label>
            <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
              <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="product-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="product-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                <SelectItem value="physical">Físico</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-8 pb-1">
            <label className="flex items-center gap-2.5 text-sm text-zinc-300">
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="product-featured-toggle" /> Destaque
            </label>
            <label className="flex items-center gap-2.5 text-sm text-zinc-300">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active-toggle" /> Ativo
            </label>
          </div>
          <div className="md:col-span-2">
            <Label>Descrição</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} data-testid="product-description-input"
              className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
          </div>
          {form.type === "digital" && (
            <div className="md:col-span-2">
              <Label>Link de entrega digital (liberado após pagamento)</Label>
              <Input value={form.digital_url} onChange={(e) => set("digital_url", e.target.value)} data-testid="product-digital-url"
                placeholder="https://..." className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
            </div>
          )}
        </section>

        <section>
          <p className="label-caps mb-5">Imagens do produto</p>
          <p className="text-[11px] text-zinc-500 mb-4">Tamanho ideal: <span className="text-gold">PNG com fundo transparente, 1000×1000 px</span> (ou proporção 3:4, ex.: 900×1200), até 2 MB. A imagem é encaixada automaticamente na janela do frame dourado e o preço aparece na placa.</p>
          <div className="space-y-3">
            {form.images.map((img, i) => (
              <div key={i} className="flex gap-2 items-center">
                <div className="flex-1"><ImageInput value={img} onChange={(v) => set("images", form.images.map((x, j) => (j === i ? v : x)))} testid={`product-image-${i}`} /></div>
                <button type="button" onClick={() => set("images", form.images.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400 p-2" data-testid="remove-image-button"><Trash2 size={14} /></button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => set("images", [...form.images, ""])}
              className="border-gold/40 text-gold hover:bg-amber-500/10" data-testid="add-image-button">
              <Plus size={13} className="mr-1.5" /> Adicionar imagem
            </Button>
          </div>
        </section>

        <section>
          <p className="label-caps mb-5">Enquadramento da imagem no frame</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-5">
              <div>
                <Label className="text-xs">Modo de encaixe</Label>
                <Select value={form.image_adjust.fit} onValueChange={(v) => set("image_adjust", { ...form.image_adjust, fit: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="adjust-fit-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10">
                    <SelectItem value="contain">Caber inteira (sem corte)</SelectItem>
                    <SelectItem value="cover">Cortar para preencher a janela</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Zoom: {form.image_adjust.scale}x</Label>
                <Slider value={[form.image_adjust.scale]} min={0.5} max={2.5} step={0.05} className="mt-3" data-testid="adjust-scale"
                  onValueChange={([v]) => set("image_adjust", { ...form.image_adjust, scale: v })} />
              </div>
              <div>
                <Label className="text-xs">Mover horizontal: {form.image_adjust.x}%</Label>
                <Slider value={[form.image_adjust.x]} min={-50} max={50} step={1} className="mt-3" data-testid="adjust-x"
                  onValueChange={([v]) => set("image_adjust", { ...form.image_adjust, x: v })} />
              </div>
              <div>
                <Label className="text-xs">Mover vertical: {form.image_adjust.y}%</Label>
                <Slider value={[form.image_adjust.y]} min={-50} max={50} step={1} className="mt-3" data-testid="adjust-y"
                  onValueChange={([v]) => set("image_adjust", { ...form.image_adjust, y: v })} />
              </div>
              <Button type="button" variant="ghost" size="sm" className="text-zinc-500 hover:text-gold" data-testid="adjust-reset"
                onClick={() => set("image_adjust", { fit: "contain", x: 0, y: 0, scale: 1 })}>
                Resetar enquadramento
              </Button>
              <div>
                <Label className="text-xs">Texto fixo do card (opcional — sobrescreve o texto global)</Label>
                <Input value={form.card_text} onChange={(e) => set("card_text", e.target.value)} data-testid="card-text-input"
                  placeholder="Ex.: Edição limitada" className="mt-1.5 bg-[#141414] border-white/10" />
              </div>
            </div>
            <div className="max-w-[230px]">
              <p className="text-[11px] text-zinc-600 mb-2">Pré-visualização ao vivo</p>
              <FramedProductImage
                image={form.images.filter(Boolean)[0]}
                name={form.name}
                price="R$ 0,00"
                adjust={form.image_adjust}
                priceDy={form.price_custom ? form.price_adjust.dy : globalPrice.dy}
                priceDx={form.price_custom ? (form.price_adjust.dx || 0) : globalPrice.dx}
                priceScale={form.price_custom ? form.price_adjust.scale : globalPrice.scale}
                priceColor={form.price_custom ? form.price_adjust.color : globalPrice.color}
                priceWidth={form.price_custom ? form.price_adjust.width : globalPrice.width}
                priceHeight={form.price_custom ? (form.price_adjust.height || 6) : globalPrice.height}
                priceRadius={form.price_custom ? (form.price_adjust.radius ?? 2) : globalPrice.radius}
                hoverZoom={false}
                testid="adjust-preview"
              />
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-5">
            <p className="label-caps">Preço na placa (largura, tamanho, cor, posição)</p>
            <label className="flex items-center gap-2.5 text-xs text-zinc-300">
              <Switch checked={form.price_custom} onCheckedChange={(v) => {
                // ao ativar, parte de onde o padrão global está salvo
                if (v && !form.price_adjust) set("price_adjust", { ...globalPrice });
                else if (v) set("price_adjust", { ...globalPrice, ...form.price_adjust });
                set("price_custom", v);
              }} data-testid="price-custom-switch" />
              Personalizar só neste produto
            </label>
          </div>
          {!form.price_custom ? (
            <p className="text-[11px] text-zinc-600 border border-white/5 rounded-md p-4 bg-black/30">
              Usando o padrão global atual (posição {globalPrice.dy}%, {globalPrice.dx}% · tamanho {globalPrice.scale}x · {globalPrice.width}% × {globalPrice.height}%). Ative a chave acima para editar a partir desses valores só neste produto.
            </p>
          ) : (
            <div className="border border-white/10 rounded-lg p-5 bg-[#111111] grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="price-adjust-panel">
              <div>
                <Label className="text-xs">Largura do fundo do preço: {form.price_adjust.width}%</Label>
                <Slider value={[form.price_adjust.width]} min={24} max={60} step={0.5} className="mt-3" data-testid="price-width"
                  onValueChange={([v]) => set("price_adjust", { ...form.price_adjust, width: v })} />
              </div>
              <div>
                <Label className="text-xs">Altura do fundo do preço: {form.price_adjust.height || 6}%</Label>
                <Slider value={[form.price_adjust.height || 6]} min={3} max={14} step={0.5} className="mt-3" data-testid="price-height"
                  onValueChange={([v]) => set("price_adjust", { ...form.price_adjust, height: v })} />
              </div>
              <div>
                <Label className="text-xs">Tamanho do preço: {form.price_adjust.scale}x</Label>
                <Slider value={[form.price_adjust.scale]} min={0.7} max={1.6} step={0.05} className="mt-3" data-testid="price-scale"
                  onValueChange={([v]) => set("price_adjust", { ...form.price_adjust, scale: v })} />
              </div>
              <div>
                <Label className="text-xs">Posição vertical: {form.price_adjust.dy}%</Label>
                <Slider value={[form.price_adjust.dy]} min={-12} max={12} step={0.5} className="mt-3" data-testid="price-dy"
                  onValueChange={([v]) => set("price_adjust", { ...form.price_adjust, dy: v })} />
              </div>
              <div>
                <Label className="text-xs">Posição horizontal (esquerda/direita): {form.price_adjust.dx || 0}%</Label>
                <Slider value={[form.price_adjust.dx || 0]} min={-20} max={20} step={0.5} className="mt-3" data-testid="price-dx"
                  onValueChange={([v]) => set("price_adjust", { ...form.price_adjust, dx: v })} />
              </div>
              <div>
                <Label className="text-xs">Cor do preço</Label>
                <div className="flex gap-2 mt-1.5 items-center">
                  <input type="color" value={form.price_adjust.color} data-testid="price-color"
                    onChange={(e) => set("price_adjust", { ...form.price_adjust, color: e.target.value })}
                    className="w-10 h-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                  <span className="text-xs font-mono text-zinc-400">{form.price_adjust.color}</span>
                </div>
              </div>
              <div>
                <Label className="text-xs">Cantos do fundo</Label>
                <Select value={String(form.price_adjust.radius ?? 2)} onValueChange={(v) => set("price_adjust", { ...form.price_adjust, radius: parseFloat(v) })}>
                  <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="price-radius"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10">
                    <SelectItem value="0">Quadrado</SelectItem>
                    <SelectItem value="1">Suave</SelectItem>
                    <SelectItem value="2">Arredondado</SelectItem>
                    <SelectItem value="4">Pill (bem redondo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Button type="button" variant="outline" size="sm" data-testid="price-apply-all"
                  className="border-gold/40 text-gold hover:bg-amber-500/10"
                  onClick={async () => {
                    try {
                      await api.put("/admin/settings", {
                        card_price_dy: form.price_adjust.dy,
                        card_price_dx: form.price_adjust.dx || 0,
                        card_price_scale: form.price_adjust.scale,
                        card_price_color: form.price_adjust.color,
                        card_price_width: form.price_adjust.width,
                        card_price_height: form.price_adjust.height || 6,
                        card_price_radius: form.price_adjust.radius ?? 2,
                      });
                      await refreshBootstrap();
                      toast.success("Formato de preço aplicado a todos os produtos");
                    } catch (err) {
                      toast.error(formatApiError(err));
                    }
                  }}>
                  Aplicar este formato para todos os produtos
                </Button>
                <p className="text-[11px] text-zinc-600 mt-2">Produtos com personalização própria ativa continuam usando a deles.</p>
              </div>
            </div>
          )}
        </section>

        <section>
          <p className="label-caps mb-5">Variações (SKU, preço e estoque por variação)</p>
          <div className="space-y-5">
            {form.variations.map((v, i) => (
              <div key={i} className="border border-white/10 rounded-lg p-5 bg-[#111111] space-y-4" data-testid={`variation-row-${i}`}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <Label className="text-xs">Nome *</Label>
                    <Input value={v.name} onChange={(e) => setVar(i, "name", e.target.value)} placeholder="30 cm" data-testid={`variation-name-${i}`}
                      className="mt-1 bg-[#141414] border-white/10" />
                  </div>
                  <div>
                    <Label className="text-xs">SKU *</Label>
                    <Input value={v.sku} onChange={(e) => setVar(i, "sku", e.target.value)} placeholder="ESC-DRG-30" data-testid={`variation-sku-${i}`}
                      className="mt-1 bg-[#141414] border-white/10" />
                  </div>
                  <div>
                    <Label className="text-xs">Preço (R$) *</Label>
                    <Input value={v.price} onChange={(e) => setVar(i, "price", e.target.value)} placeholder="449,90" data-testid={`variation-price-${i}`}
                      className="mt-1 bg-[#141414] border-white/10" />
                  </div>
                  <div>
                    <Label className="text-xs">Promoção (R$)</Label>
                    <Input value={v.promo_price || ""} onChange={(e) => setVar(i, "promo_price", e.target.value)} placeholder="399,90" data-testid={`variation-promo-${i}`}
                      className="mt-1 bg-[#141414] border-white/10" />
                  </div>
                  <div>
                    <Label className="text-xs">Estoque</Label>
                    <Input type="number" value={v.stock} onChange={(e) => setVar(i, "stock", e.target.value)} data-testid={`variation-stock-${i}`}
                      className="mt-1 bg-[#141414] border-white/10" />
                  </div>
                </div>
                <ImageInput value={(v.images || [])[0] || ""} onChange={(url) => setVar(i, "images", url ? [url] : [])} testid={`variation-image-${i}`} />
                <button type="button" onClick={() => set("variations", form.variations.filter((_, j) => j !== i))}
                  className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1" data-testid={`remove-variation-${i}`}>
                  <Trash2 size={12} /> Remover variação
                </button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => set("variations", [...form.variations, emptyVar()])}
              className="border-gold/40 text-gold hover:bg-amber-500/10" data-testid="add-variation-button">
              <Plus size={13} className="mr-1.5" /> Adicionar variação
            </Button>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label>Meta title (SEO)</Label>
            <Input value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} data-testid="product-meta-title"
              className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
          </div>
          <div>
            <Label>Meta description (SEO)</Label>
            <Input value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} data-testid="product-meta-description"
              className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
          </div>
        </section>

        <Button type="submit" disabled={saving} data-testid="save-product-button"
          className="bg-gold text-black hover:bg-gold-light font-semibold px-10 h-11 transition-colors">
          {saving ? "Salvando..." : "Salvar produto"}
        </Button>
      </form>
    </div>
  );
}
